import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import {
  applyAwardRecalculationBatch,
  checkAwardRecalculation,
  previewAwardRecalculationBatch,
} from "../../utils/deliveryRecordsApi";

/** Pause between batches so a full season pass never hits the database in a burst. */
const BATCH_PAUSE_MS = 300;
const ROWS_PER_PAGE = 50;

/** Pause between apply batches; longer spreads the disk writes out further. */
const APPLY_PAUSES_MS = [500, 1000, 2000, 5000];
/** Waits before retrying a failed apply batch; after the last one the run stops for a manual resume. */
const APPLY_RETRY_DELAYS_MS = [2000, 5000, 10000];
/** How long to wait while another admin's batch holds the run lock. */
const APPLY_BUSY_WAIT_MS = 3000;
const APPLY_MAX_BUSY_WAITS = 40;

/**
 * Gameweek achievement ids, in the order the page lists them.
 * team_over_120_points is the 200+ tier (it keeps its original Gaming Hub id).
 */
const ACHIEVEMENTS = [
  "team_over_100_points",
  "team_over_150_points",
  "team_over_120_points",
  "all_strikers_scored",
  "all_defenders_clean_sheet",
  "all_midfielders_assist",
  "hat_trick",
  "striker_5_shots_on_target",
  "top_20pct_leaderboard",
  "top_10pct_leaderboard",
];

const idleApply = () => ({
  status: "idle", // idle | running | stopped | done | failed
  error: null,
  notice: null,
  scanned: 0,
  inserted: 0,
  // The cursor after the last committed batch; a resume continues from here.
  afterTeamId: null,
  startedAt: null,
  finishedAt: null,
});

/**
 * new: earned at least one achievement its user does not hold yet.
 * unchanged: its user already holds every achievement it earned.
 * none: earned nothing in any finalised gameweek.
 */
const OUTCOMES = ["new", "unchanged", "none", "skipped"];

const outcomeColors = {
  new: "info",
  unchanged: "default",
  none: "default",
  skipped: "error",
};

const SKIP_REASONS = {
  no_snapshot: "No snapshot in a finalised gameweek",
  no_gaming_hub_id: "No Gaming Hub ID",
};

const emptyTotals = () => ({
  new: 0,
  unchanged: 0,
  none: 0,
  skipped: 0,
  no_snapshot: 0,
  no_gaming_hub_id: 0,
  // Achievements to add, across every team.
  missing: 0,
  // Teams missing each achievement.
  byAchievement: Object.fromEntries(ACHIEVEMENTS.map((id) => [id, 0])),
});

const compactTable = {
  "& .MuiTableCell-root": { fontSize: 12, py: 0.5, px: 1 },
  "& .MuiTableCell-head": { fontWeight: 600 },
  "& .MuiChip-root": { height: 20, fontSize: 11 },
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatSeconds = (seconds) =>
  seconds >= 60
    ? `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
    : `${Math.round(seconds)}s`;

const display = (value) =>
  value === null || value === undefined ? "—" : value;

/** Achievement chips: plain ids, or { event_id, gameweek_number } shown with their gameweek. */
const AchievementList = ({ items, color }) =>
  items.length === 0 ? (
    "—"
  ) : (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {items.map((item) =>
        typeof item === "string" ? (
          <Chip key={item} label={item} color={color} size="small" />
        ) : (
          <Chip
            key={item.event_id}
            label={`${item.event_id} · GW${item.gameweek_number}`}
            color={color}
            size="small"
          />
        )
      )}
    </Box>
  );

/**
 * Season achievement re-calculator. Every achievement is once per user per
 * tournament, so one pass walks every team of the active tournament in keyset
 * batches, checks every finalised gameweek, and shows each achievement the team
 * earned at the earliest gameweek it earned it, and which of those the user does
 * not hold yet. Re-calculate only reads; Apply adds the missing ones. Held
 * achievements are never changed, removed or repeated, because Gaming Hub
 * cannot take an achievement back.
 */
const AwardRecalculatorDialog = ({ open, onClose }) => {
  const [check, setCheck] = useState({
    loading: false,
    error: null,
    data: null,
  });
  const [run, setRun] = useState({
    status: "idle", // idle | running | stopped | done | failed
    error: null,
    scanned: 0,
    startedAt: null,
    finishedAt: null,
  });
  const [totals, setTotals] = useState(emptyTotals);
  // Every row of the run; kept in a ref so a 200k-team pass does not copy arrays per batch.
  const rowsRef = useRef([]);
  const [rowsVersion, setRowsVersion] = useState(0);
  const runIdRef = useRef(0);
  // "changes", "all", an outcome, a skip reason, or an achievement id.
  const [outcomeFilter, setOutcomeFilter] = useState("changes");
  const [teamSearch, setTeamSearch] = useState("");
  const [page, setPage] = useState(0);
  const [apply, setApply] = useState(idleApply);
  const [applyPauseMs, setApplyPauseMs] = useState(1000);
  const applyIdRef = useRef(0);
  // Which confirmation is open: "apply" before writing, "close" when leaving mid-write.
  const [confirming, setConfirming] = useState(null);

  const running = run.status === "running";
  const applying = apply.status === "running";

  const loadCheck = async () => {
    setCheck({ loading: true, error: null, data: null });
    try {
      const data = await checkAwardRecalculation();
      setCheck({ loading: false, error: null, data });
    } catch (err) {
      console.error("Failed to check season achievements:", err);
      setCheck({
        loading: false,
        error:
          err?.response?.data?.message ||
          "Failed to check the season. Close and open the dialog to try again.",
        data: null,
      });
    }
  };

  useEffect(() => {
    if (open && !check.data && !check.loading && !check.error) loadCheck();
    // Checked once per opening; the check also refreshes the percentile cut-offs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resetRun = () => {
    runIdRef.current += 1;
    rowsRef.current = [];
    setRowsVersion((version) => version + 1);
    setTotals(emptyTotals());
    setRun({
      status: "idle",
      error: null,
      scanned: 0,
      startedAt: null,
      finishedAt: null,
    });
    setPage(0);
  };

  const startRun = async () => {
    resetRun();
    // A fresh preview supersedes a finished apply; a stopped or failed one
    // keeps its Resume.
    if (apply.status === "done") setApply(idleApply());
    const runId = runIdRef.current;
    const startedAt = Date.now();
    setRun({
      status: "running",
      error: null,
      scanned: 0,
      startedAt,
      finishedAt: null,
    });

    let afterTeamId = null;
    let scanned = 0;
    try {
      do {
        const batch = await previewAwardRecalculationBatch(afterTeamId);
        // Stopped or restarted while this batch was in flight.
        if (runIdRef.current !== runId) return;

        rowsRef.current.push(...batch.rows);
        scanned += batch.rows.length;
        setTotals((prev) => {
          const next = { ...prev, byAchievement: { ...prev.byAchievement } };
          for (const row of batch.rows) {
            next[row.outcome] += 1;
            if (row.skip_reason) next[row.skip_reason] += 1;
            next.missing += row.missing.length;
            for (const { event_id: id } of row.missing) {
              next.byAchievement[id] = (next.byAchievement[id] ?? 0) + 1;
            }
          }
          return next;
        });
        setRowsVersion((version) => version + 1);
        const scannedSoFar = scanned;
        setRun((prev) => ({ ...prev, scanned: scannedSoFar }));

        afterTeamId = batch.next_after_team_id;
        if (afterTeamId) await sleep(BATCH_PAUSE_MS);
      } while (afterTeamId && runIdRef.current === runId);

      if (runIdRef.current !== runId) return;
      setRun((prev) => ({ ...prev, status: "done", finishedAt: Date.now() }));
    } catch (err) {
      if (runIdRef.current !== runId) return;
      console.error("Achievement re-calculation batch failed:", err);
      setRun((prev) => ({
        ...prev,
        status: "failed",
        finishedAt: Date.now(),
        error: `A batch failed after ${scanned.toLocaleString(
          "en-GB"
        )} teams. Run it again to retry.`,
      }));
    }
  };

  /**
   * Writes the season in committed batches. A failed batch is retried from
   * the same cursor; if it keeps failing the run stops and can be resumed from
   * the last committed batch. Re-running from the start is also safe:
   * achievements already recorded are not written again.
   */
  const runApply = async ({ resume }) => {
    applyIdRef.current += 1;
    const applyId = applyIdRef.current;
    const isCurrent = () => applyIdRef.current === applyId;

    let afterTeamId = resume ? apply.afterTeamId : null;
    const counts = resume
      ? { scanned: apply.scanned, inserted: apply.inserted }
      : { scanned: 0, inserted: 0 };
    setApply((prev) => ({
      ...(resume ? prev : idleApply()),
      ...counts,
      afterTeamId,
      status: "running",
      error: null,
      notice: null,
      startedAt: resume ? prev.startedAt : Date.now(),
      finishedAt: null,
    }));

    do {
      let batch = null;
      let attempt = 0;
      let busyWaits = 0;
      while (!batch) {
        try {
          batch = await applyAwardRecalculationBatch(afterTeamId);
        } catch (err) {
          if (!isCurrent()) return;
          const status = err?.response?.status;
          const message = err?.response?.data?.message;
          // Another batch holds the run lock: wait for it rather than count a failure.
          if (status === 409 && /retry shortly/.test(message ?? "")) {
            busyWaits += 1;
            if (busyWaits <= APPLY_MAX_BUSY_WAITS) {
              setApply((prev) => ({
                ...prev,
                notice: "Waiting for another re-calculation batch…",
              }));
              await sleep(APPLY_BUSY_WAIT_MS);
              if (!isCurrent()) return;
              continue;
            }
          }
          // Anything else that is a definite refusal is not worth retrying.
          const retryable = !status || status >= 500;
          if (!retryable || attempt >= APPLY_RETRY_DELAYS_MS.length) {
            setApply((prev) => ({
              ...prev,
              status: "failed",
              notice: null,
              finishedAt: Date.now(),
              error: `${
                message || "A batch failed."
              } ${prev.scanned.toLocaleString(
                "en-GB"
              )} teams are already written and kept. Resume to continue from there.`,
            }));
            return;
          }
          const retryNotice = `Batch failed, retrying (${attempt + 1} of ${
            APPLY_RETRY_DELAYS_MS.length
          })…`;
          setApply((prev) => ({ ...prev, notice: retryNotice }));
          await sleep(APPLY_RETRY_DELAYS_MS[attempt]);
          attempt += 1;
          if (!isCurrent()) return;
        }
      }
      if (!isCurrent()) return;

      // The batch is committed: record its cursor before anything else can fail.
      afterTeamId = batch.next_after_team_id;
      const committedCursor = afterTeamId;
      setApply((prev) => ({
        ...prev,
        notice: null,
        scanned: prev.scanned + batch.scanned,
        inserted: prev.inserted + batch.inserted,
        afterTeamId: committedCursor,
      }));

      if (afterTeamId) await sleep(applyPauseMs);
    } while (afterTeamId && isCurrent());

    if (!isCurrent()) return;
    setApply((prev) => ({
      ...prev,
      status: "done",
      notice: null,
      finishedAt: Date.now(),
    }));
  };

  const startApply = () => setConfirming("apply");

  const confirmApply = () => {
    setConfirming(null);
    runApply({ resume: false });
  };

  const stopApply = () => {
    applyIdRef.current += 1;
    setApply((prev) => ({
      ...prev,
      status: "stopped",
      notice: null,
      finishedAt: Date.now(),
    }));
  };

  const stopRun = () => {
    runIdRef.current += 1;
    setRun((prev) => ({ ...prev, status: "stopped", finishedAt: Date.now() }));
  };

  const handleClose = () => {
    if (applying) {
      setConfirming("close");
      return;
    }
    closeNow();
  };

  const closeNow = () => {
    setConfirming(null);
    // Leaving the dialog stops any batch loop still running.
    runIdRef.current += 1;
    applyIdRef.current += 1;
    if (applying) {
      setApply((prev) => ({
        ...prev,
        status: "stopped",
        notice: null,
        finishedAt: Date.now(),
      }));
    }
    if (running) {
      setRun((prev) => ({
        ...prev,
        status: "stopped",
        finishedAt: Date.now(),
      }));
    }
    onClose();
  };

  const filteredRows = useMemo(() => {
    const search = teamSearch.trim().toLowerCase();
    return rowsRef.current.filter((row) => {
      if (outcomeFilter === "changes" && row.outcome !== "new") return false;
      if (OUTCOMES.includes(outcomeFilter) && row.outcome !== outcomeFilter)
        return false;
      if (SKIP_REASONS[outcomeFilter] && row.skip_reason !== outcomeFilter)
        return false;
      if (
        ACHIEVEMENTS.includes(outcomeFilter) &&
        !row.missing.some((item) => item.event_id === outcomeFilter)
      )
        return false;
      if (
        search &&
        !row.fantasy_team_id.toLowerCase().includes(search) &&
        !(row.team_name || "").toLowerCase().includes(search) &&
        !(row.username || "").toLowerCase().includes(search)
      ) {
        return false;
      }
      return true;
    });
    // rowsVersion stands in for the ref's contents changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsVersion, outcomeFilter, teamSearch]);

  const pageRows = filteredRows.slice(
    page * ROWS_PER_PAGE,
    (page + 1) * ROWS_PER_PAGE
  );

  const totalTeams = check.data?.total_teams ?? 0;
  const progress =
    totalTeams > 0 ? Math.min(100, (run.scanned / totalTeams) * 100) : 0;
  const elapsedSeconds = run.startedAt
    ? ((run.finishedAt ?? Date.now()) - run.startedAt) / 1000
    : 0;
  const remainingSeconds =
    running && run.scanned > 0
      ? (elapsedSeconds / run.scanned) * Math.max(totalTeams - run.scanned, 0)
      : null;

  const applyProgress =
    totalTeams > 0 ? Math.min(100, (apply.scanned / totalTeams) * 100) : 0;
  const applyElapsedSeconds = apply.startedAt
    ? ((apply.finishedAt ?? Date.now()) - apply.startedAt) / 1000
    : 0;

  const selectFilter = (key) => {
    setOutcomeFilter(key);
    setPage(0);
  };

  const summaryChip = (key, label, count, color) => (
    <Chip
      key={key}
      label={`${label}: ${count.toLocaleString("en-GB")}`}
      color={color}
      variant={outcomeFilter === key ? "filled" : "outlined"}
      onClick={() => selectFilter(key)}
      size="small"
    />
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{ sx: { width: "95vw", height: "92vh", maxWidth: "none" } }}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}
      >
        <Box sx={{ flexGrow: 1 }}>
          Re-calculate season achievements
          <Typography variant="body2" color="text.secondary">
            Checks every finalised gameweek for every team and finds the
            achievements each team earned, at the earliest gameweek it earned
            them. Each achievement is given once per user per season:
            Re-calculate only reads; Apply adds the ones the user does not hold
            yet, silently. Held achievements are never changed or repeated.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          // Only the table may shrink; everything else keeps its height so
          // the pager below the table is never squeezed out of view.
          "& > :not(.MuiTableContainer-root)": { flexShrink: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {running ? (
            <Button variant="outlined" color="warning" onClick={stopRun}>
              Stop
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={!check.data || check.loading || applying}
              onClick={startRun}
            >
              Re-calculate
            </Button>
          )}
          {applying ? (
            <Button variant="outlined" color="warning" onClick={stopApply}>
              Stop writing
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                color="warning"
                // A finished apply makes the preview's counts stale; the
                // next Re-calculate re-enables it.
                disabled={
                  run.status !== "done" ||
                  totals.missing === 0 ||
                  apply.status === "done"
                }
                onClick={startApply}
              >
                Apply
              </Button>
              {["failed", "stopped"].includes(apply.status) &&
                apply.afterTeamId && (
                  <Button
                    variant="outlined"
                    onClick={() => runApply({ resume: true })}
                  >
                    Resume
                  </Button>
                )}
            </>
          )}
          <TextField
            select
            label="Pause between writes"
            value={applyPauseMs}
            onChange={(e) => setApplyPauseMs(Number(e.target.value))}
            disabled={applying}
            size="small"
            sx={{ minWidth: 170 }}
          >
            {APPLY_PAUSES_MS.map((ms) => (
              <MenuItem key={ms} value={ms}>
                {ms / 1000}s
              </MenuItem>
            ))}
          </TextField>
          {check.loading && <CircularProgress size={20} />}
        </Box>

        {check.error && <Alert severity="error">{check.error}</Alert>}

        {check.data && (
          <Alert severity="info">
            {[
              check.data.tournament.competition_name,
              check.data.tournament.season_label,
            ]
              .filter(Boolean)
              .join(" ")}
            : checks{" "}
            {check.data.gameweeks.map((item) => `GW${item.number}`).join(", ")}{" "}
            (finalised). Users hold{" "}
            {check.data.held_achievements.toLocaleString("en-GB")} gameweek
            achievements already. A full pass scans{" "}
            {check.data.total_teams.toLocaleString("en-GB")} teams in batches of
            5,000. Best run outside match hours.
          </Alert>
        )}

        {run.status !== "idle" && (
          <Box>
            <LinearProgress
              variant={totalTeams > 0 ? "determinate" : "indeterminate"}
              value={progress}
              color={run.status === "failed" ? "error" : "primary"}
            />
            <Typography variant="caption" color="text.secondary">
              {run.scanned.toLocaleString("en-GB")} of{" "}
              {totalTeams.toLocaleString("en-GB")} teams
              {" · "}
              {formatSeconds(elapsedSeconds)} elapsed
              {remainingSeconds !== null &&
                ` · about ${formatSeconds(remainingSeconds)} left`}
              {run.status === "done" && " · complete"}
              {run.status === "stopped" && " · stopped"}
            </Typography>
          </Box>
        )}

        {run.error && <Alert severity="error">{run.error}</Alert>}

        {apply.status !== "idle" && (
          <Box>
            <LinearProgress
              variant={totalTeams > 0 ? "determinate" : "indeterminate"}
              value={applyProgress}
              color={apply.status === "failed" ? "error" : "warning"}
            />
            <Typography variant="caption" color="text.secondary">
              Writing: {apply.scanned.toLocaleString("en-GB")} of{" "}
              {totalTeams.toLocaleString("en-GB")} teams ·{" "}
              {apply.inserted.toLocaleString("en-GB")} achievements added
              {" · "}
              {formatSeconds(applyElapsedSeconds)} elapsed
              {apply.status === "done" && " · complete"}
              {apply.status === "stopped" &&
                " · stopped (written batches kept)"}
            </Typography>
          </Box>
        )}

        {apply.notice && <Alert severity="info">{apply.notice}</Alert>}
        {apply.error && <Alert severity="error">{apply.error}</Alert>}
        {apply.status === "done" && (
          <Alert severity="success">
            Wrote the season: {apply.inserted.toLocaleString("en-GB")}{" "}
            achievements added. Re-calculate again to confirm no team shows as
            new.
          </Alert>
        )}

        {run.status !== "idle" && (
          <>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Chip
                label="All changes"
                variant={outcomeFilter === "changes" ? "filled" : "outlined"}
                onClick={() => selectFilter("changes")}
                size="small"
              />
              {summaryChip("new", "New", totals.new, outcomeColors.new)}
              {summaryChip(
                "unchanged",
                "Unchanged",
                totals.unchanged,
                outcomeColors.unchanged
              )}
              {summaryChip(
                "none",
                "No achievements",
                totals.none,
                outcomeColors.none
              )}
              {summaryChip(
                "skipped",
                "Skipped",
                totals.skipped,
                outcomeColors.skipped
              )}
              {Object.entries(SKIP_REASONS).map(([key, label]) =>
                summaryChip(key, label, totals[key], "default")
              )}
              <TextField
                label="Find team, team ID or username"
                value={teamSearch}
                onChange={(e) => {
                  setTeamSearch(e.target.value);
                  setPage(0);
                }}
                size="small"
                sx={{ minWidth: 300, ml: "auto" }}
              />
              <TextField
                select
                label="Show"
                value={outcomeFilter}
                onChange={(e) => selectFilter(e.target.value)}
                size="small"
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="changes">All changes</MenuItem>
                <MenuItem value="all">Every team</MenuItem>
                {OUTCOMES.map((outcome) => (
                  <MenuItem key={outcome} value={outcome}>
                    {outcome}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Missing, by achievement (
                {totals.missing.toLocaleString("en-GB")} in all):
              </Typography>
              {ACHIEVEMENTS.map((id) =>
                summaryChip(id, id, totals.byAchievement[id] ?? 0, "info")
              )}
            </Box>

            <TableContainer
              component={Paper}
              variant="outlined"
              // Fills the space left and scrolls inside it, under a sticky header.
              sx={{ flex: "1 1 0", minHeight: 240, overflow: "auto" }}
            >
              <Table size="small" stickyHeader sx={compactTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>Team</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Gameweeks played</TableCell>
                    <TableCell align="right">Earned</TableCell>
                    <TableCell>Already held</TableCell>
                    <TableCell>To add (earliest gameweek)</TableCell>
                    <TableCell>Outcome</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {running
                            ? "Calculating…"
                            : "No teams match this view"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageRows.map((row) => (
                      <TableRow key={row.fantasy_team_id} hover>
                        <TableCell>
                          <Box sx={{ fontSize: 12 }}>
                            {row.team_name || "—"}
                          </Box>
                          <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                            {row.fantasy_team_id}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ fontSize: 12 }}>{row.username || "—"}</Box>
                          <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                            {display(row.engagecraft_id)}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {row.gameweeks_played}
                        </TableCell>
                        <TableCell align="right">
                          {row.qualified.length}
                        </TableCell>
                        <TableCell>
                          <AchievementList items={row.awarded} />
                        </TableCell>
                        <TableCell>
                          <AchievementList items={row.missing} color="info" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              row.skip_reason
                                ? `${row.outcome}: ${
                                    SKIP_REASONS[row.skip_reason]
                                  }`
                                : row.outcome
                            }
                            color={outcomeColors[row.outcome] || "default"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={ROWS_PER_PAGE}
              rowsPerPageOptions={[]}
              sx={{ "& .MuiTablePagination-displayedRows": { fontSize: 12 } }}
            />
          </>
        )}
      </DialogContent>

      <Dialog
        open={confirming === "apply"}
        onClose={() => setConfirming(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add missing season achievements?</DialogTitle>
        <DialogContent>
          <Box
            component="dl"
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              rowGap: 0.5,
              m: 0,
              "& dt": { color: "text.secondary" },
              "& dd": { m: 0, fontWeight: 600, textAlign: "right" },
            }}
          >
            <dt>Teams with new achievements</dt>
            <dd>{totals.new.toLocaleString("en-GB")}</dd>
            <dt>Achievements to add</dt>
            <dd>{totals.missing.toLocaleString("en-GB")}</dd>
            <dt>Teams to scan</dt>
            <dd>{totalTeams.toLocaleString("en-GB")}</dd>
            <dt>Pause between writes</dt>
            <dd>{applyPauseMs / 1000}s</dd>
          </Box>
          <DialogContentText sx={{ mt: 2, fontSize: 13 }}>
            Achievements are worked out again as each batch is written, and only
            ones the user does not hold yet are added, each dated to the
            earliest gameweek it was earned in and sent without a notification.
            Each batch is saved on its own, so stopping keeps what is done.
            Added achievements are queued for Gaming Hub, which cannot take an
            achievement back, so check the preview first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmApply}>
            Add {totals.missing.toLocaleString("en-GB")} achievements
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirming === "close"}
        onClose={() => setConfirming(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Stop writing and close?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            {apply.scanned.toLocaleString("en-GB")} teams are already written
            and kept. Running Apply again later skips every achievement already
            held and continues safely.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)}>Keep writing</Button>
          <Button variant="contained" color="warning" onClick={closeNow}>
            Stop and close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AwardRecalculatorDialog;
